import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from './Modal';
import Button from './Button';

export const CONFIRMATION_PHRASE = 'EXCLUIR';

/**
 * Confirmação de exclusão de conta.
 *
 * Regra de negócio: a ação é irreversível e não tem janela de arrependimento,
 * por isso o botão só habilita quando o usuário digita a palavra de confirmação.
 * O texto lista o que é apagado e o que permanece, para que a decisão seja
 * informada antes do clique.
 */
const DeleteAccountModal = ({ isOpen, onClose, onConfirm, contributionCount }) => {
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setConfirmation('');
      setError(null);
      setLoading(false);
    }
  }, [isOpen]);

  const canConfirm = confirmation.trim().toUpperCase() === CONFIRMATION_PHRASE && !loading;

  // O `!` vence as classes verdes padrão do Button, cuja ordem no CSS gerado
  // não é garantida. Quando desabilitado, deixa o cinza do Button aparecer.
  const dangerButtonClass = canConfirm ? '!bg-red-700 hover:enabled:!bg-red-600' : '';

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setLoading(true);
    setError(null);
    const result = await onConfirm(confirmation.trim().toUpperCase());
    if (!result?.success) {
      setError(result?.message || 'Não foi possível excluir a conta.');
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      title="Excluir conta"
      onClose={onClose}
      dismissible={!loading}
      footer={(
        <>
          <Button className="secondary" onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button
            className={dangerButtonClass}
            onClick={handleConfirm}
            disabled={!canConfirm}
            loading={loading}
          >
            Excluir minha conta
          </Button>
        </>
      )}
    >
      <p className="font-semibold text-red-700 dark:text-red-300">
        Esta ação é permanente e não poderá ser revertida.
      </p>

      <div>
        <p className="font-medium mb-1">Serão apagados:</p>
        <ul className="list-disc pl-5 space-y-0.5">
          <li>Seus dados de cadastro: nome, e-mail, telefone, data de nascimento e área de atuação</li>
          <li>Seu WhatsApp e os consentimentos de contato</li>
          <li>Seus códigos de acesso e o histórico de notificações</li>
          <li>Suas contribuições ainda em análise, junto com os arquivos anexados</li>
          <li>Suas recompensas do programa de contribuições, incluindo acessos vitalícios</li>
        </ul>
      </div>

      <div>
        <p className="font-medium mb-1">Permanecem no acervo, sem vínculo com você:</p>
        <ul className="list-disc pl-5 space-y-0.5">
          <li>
            Contribuições já aprovadas ou respondidas
            {contributionCount > 0 && ` (${contributionCount} até agora)`} — elas passaram a integrar
            as evidências da plataforma e não podem ser retiradas
          </li>
        </ul>
      </div>

      <p>
        Você perderá o acesso imediatamente e não será possível recuperar a conta. Um novo cadastro
        com o mesmo e-mail ou telefone começará do zero.
      </p>

      <div>
        <label htmlFor="delete-account-confirmation" className="block font-medium mb-1">
          Para confirmar, digite <span className="font-mono">{CONFIRMATION_PHRASE}</span>:
        </label>
        <input
          id="delete-account-confirmation"
          type="text"
          autoComplete="off"
          className="w-full rounded-lg border px-3 py-2 text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          placeholder={CONFIRMATION_PHRASE}
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          disabled={loading}
        />
      </div>

      {error && <p className="text-red-600 dark:text-red-300">{error}</p>}
    </Modal>
  );
};

DeleteAccountModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  contributionCount: PropTypes.number,
};

DeleteAccountModal.defaultProps = {
  contributionCount: 0,
};

export default DeleteAccountModal;
